import { useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { 
  CalendarIcon, 
  BarChart3, 
  PieChart, 
  TrendingUp, 
  Save, 
  Eye, 
  Target, 
  ArrowRight,
  ArrowLeft,
  Filter,
  Settings,
  LineChart,
  Calculator
} from 'lucide-react';
import { format } from 'date-fns';
import { ReportConfig, AnalyticsPeriod } from '../types';
import { Chart } from './Chart';
import { useAnalytics } from '../hooks/useAnalytics';
import { toast } from 'sonner';
import { MetricCard } from './MetricCard';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useCategoriesStore } from '@/store/categoriesStore';

interface ReportBuilderProps {
  onSave?: (report: ReportConfig) => void;
  onCancel?: () => void;
  transactions?: any[];
  selectedPeriod?: AnalyticsPeriod;
  mainCurrencySymbol?: string;
  convertToDefault?: (amount: number, fromCurrency: string) => number;
  accounts?: any[];
  defaultCurrency?: string;
}

const CHART_TYPES = [
  { value: 'line', label: 'Line Chart', icon: TrendingUp, description: 'Show trends over time' },
  { value: 'bar', label: 'Bar Chart', icon: BarChart3, description: 'Compare values across categories' },
  { value: 'area', label: 'Area Chart', icon: LineChart, description: 'Visualize cumulative data' },
  { value: 'pie', label: 'Pie Chart', icon: PieChart, description: 'Show proportional data' },
  { value: 'doughnut', label: 'Doughnut Chart', icon: PieChart, description: 'Modern pie chart style' },
] as const;

const REPORT_TYPES = [
  { 
    value: 'income_expense', 
    label: 'Income vs Expenses',
    description: 'Compare your income and spending patterns',
    icon: TrendingUp,
    color: 'text-blue-500 dark:text-blue-400'
  },
  { 
    value: 'category_breakdown', 
    label: 'Category Breakdown',
    description: 'Analyze spending by category',
    icon: PieChart,
    color: 'text-green-500 dark:text-green-400'
  },
  { 
    value: 'trend_analysis', 
    label: 'Trend Analysis',
    description: 'Track changes over time',
    icon: LineChart,
    color: 'text-purple-500 dark:text-purple-400'
  },
  { 
    value: 'comparison', 
    label: 'Period Comparison',
    description: 'Compare different time periods',
    icon: BarChart3,
    color: 'text-orange-500 dark:text-orange-400'
  },
  { 
    value: 'payee_analysis', 
    label: 'Payee Analysis',
    description: 'Analyze spending by payee/merchant',
    icon: Target,
    color: 'text-pink-500 dark:text-pink-400'
  },
] as const;

const STEP_CONFIG = [
  {
    id: 'basics',
    title: 'Report Basics',
    description: 'Name and describe your report',
    icon: Settings
  },
  {
    id: 'type',
    title: 'Report Type',
    description: 'Choose what to analyze',
    icon: BarChart3
  },
  {
    id: 'period',
    title: 'Time Period',
    description: 'Select date range',
    icon: CalendarIcon
  },
  {
    id: 'filters',
    title: 'Filters & Options',
    description: 'Customize data filters',
    icon: Filter
  },
  {
    id: 'preview',
    title: 'Preview & Save',
    description: 'Review and save your report',
    icon: Eye
  }
];

export function ReportBuilder({ onSave, onCancel, transactions = [], mainCurrencySymbol = '$', convertToDefault, accounts = [], defaultCurrency }: ReportBuilderProps) {
  const { periods, isLoading } = useAnalytics();
  const { categories } = useCategoriesStore();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [reportConfig, setReportConfig] = useState<ReportConfig>({
    name: '',
    description: '',
    type: 'income_expense',
    chartType: 'line',
    period: periods[2] || {
      label: 'Last 3 Months',
      value: '3m',
      startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
      endDate: new Date()
    },
    filters: {
      transactionTypes: ['income', 'expense'],
      categories: [],
      payees: [],
      accounts: [],
      tags: [],
      minAmount: undefined,
      maxAmount: undefined,
      includedCategories: [],
      excludedCategories: [],
      includedPayees: [],
      excludedPayees: []
    },
    includeMetrics: ['totalIncome', 'totalExpenses', 'netSavings'],
    categories: [],
    createdAt: new Date(),
    updatedAt: new Date()
  });
  
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [isCustomDate, setIsCustomDate] = useState(false);

  // Available metrics for selection
  const availableMetrics = [
    { id: 'totalIncome', label: 'Total Income', icon: TrendingUp, description: 'Sum of all income' },
    { id: 'totalExpenses', label: 'Total Expenses', icon: TrendingUp, description: 'Sum of all expenses' },
    { id: 'netSavings', label: 'Net Savings', icon: Target, description: 'Income minus expenses' },
    { id: 'avgDailySpending', label: 'Avg Daily Spending', icon: Calculator, description: 'Average spending per day' },
    { id: 'savingsRate', label: 'Savings Rate', icon: Target, description: 'Percentage of income saved' },
    { id: 'transactionCount', label: 'Transaction Count', icon: BarChart3, description: 'Total number of transactions' }
  ];

  // Available categories (derived from transactions and categories store)
  const availableCategories: string[] = useMemo(() => {
    const categoryNames = new Set<string>();
    
    transactions.forEach((t: any) => {
      let categoryName = '';
      
      // First try to get category name directly if it exists
      if (t.category && typeof t.category === 'string') {
        categoryName = t.category.trim();
      } else if (t.categoryName && typeof t.categoryName === 'string') {
        categoryName = t.categoryName.trim();
      } else if (t.category_name && typeof t.category_name === 'string') {
        categoryName = t.category_name.trim();
      }
      
      // If no direct category name, try to resolve from category_id
      if (!categoryName && (t.category_id || t.categoryId)) {
        const categoryId = t.category_id || t.categoryId;
        
        // Look up the category name from the categories store
        for (const mainCategory of categories) {
          // Check if this is the main category ID
          if (mainCategory.id === categoryId) {
            categoryName = mainCategory.name;
            break;
          }
          
          // Check subcategories
          if (mainCategory.subcategories) {
            const subcategory = mainCategory.subcategories.find((sub: any) => sub.id === categoryId);
            if (subcategory) {
              categoryName = `${mainCategory.name} - ${subcategory.name}`;
              break;
            }
          }
        }
        
        // If we still can't resolve the category, use a fallback
        if (!categoryName && categoryId) {
          categoryName = `Category ${categoryId.substring(0, 8)}...`;
        }
      }
      
      if (categoryName) {
        categoryNames.add(categoryName);
      }
    });
    
    return Array.from(categoryNames).sort();
  }, [transactions, categories]);

  // Available payees (derived from transactions)
  const availablePayees = useMemo(() => {
    const payees = new Set(
      transactions
        .map(t => t.payee || t.merchant || t.description)
        .filter(Boolean)
        .map(p => typeof p === 'string' ? p.trim() : String(p).trim())
        .filter(p => p.length > 0)
    );
    return Array.from(payees).sort();
  }, [transactions]);

  // Helper function to resolve category name from transaction
  const getCategoryName = (transaction: any): string => {
    // First try to get category name directly if it exists
    if (transaction.category && typeof transaction.category === 'string') {
      return transaction.category.trim();
    }
    if (transaction.categoryName && typeof transaction.categoryName === 'string') {
      return transaction.categoryName.trim();
    }
    if (transaction.category_name && typeof transaction.category_name === 'string') {
      return transaction.category_name.trim();
    }
    
    // If no direct category name, try to resolve from category_id
    if (transaction.category_id || transaction.categoryId) {
      const categoryId = transaction.category_id || transaction.categoryId;
      
      // Look up the category name from the categories store
      for (const mainCategory of categories) {
        // Check if this is the main category ID
        if (mainCategory.id === categoryId) {
          return mainCategory.name;
        }
        
        // Check subcategories
        if (mainCategory.subcategories) {
          const subcategory = mainCategory.subcategories.find((sub: any) => sub.id === categoryId);
          if (subcategory) {
            return `${mainCategory.name} - ${subcategory.name}`;
          }
        }
      }
    }
    
    return 'Uncategorized';
  };

  // Helper function to get main category name only and filter out uncategorized
  const getMainCategoryName = (transaction: any): string | null => {
    // If no category_id, this is uncategorized - exclude it
    if (!transaction.category_id && !transaction.categoryId) {
      return null;
    }

    const categoryId = transaction.category_id || transaction.categoryId;
    
    // Look up the category in the categories store
    for (const mainCategory of categories) {
      // Check if this is the main category ID
      if (mainCategory.id === categoryId) {
        return mainCategory.name;
      }
      
      // Check subcategories - return main category name
      if (mainCategory.subcategories) {
        const subcategory = mainCategory.subcategories.find((sub: any) => sub.id === categoryId);
        if (subcategory) {
          return mainCategory.name; // Return main category name, not subcategory
        }
      }
    }
    
    // If category not found in store, exclude it
    return null;
  };

  // Helper function to resolve category color from transaction
  const getCategoryColor = (transaction: any): string => {
    // Default colors as fallback
    const defaultColors = [
      '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
      '#8B5CF6', '#06B6D4', '#EC4899', '#F97316',
      '#A855F7', '#14B8A6', '#84CC16', '#6366F1'
    ];

    // If no category ID, use first default color
    if (!transaction.category_id && !transaction.categoryId) {
      return defaultColors[0];
    }

    const categoryId = transaction.category_id || transaction.categoryId;
    
    // Look up the category color from the categories store
    for (const mainCategory of categories) {
      // Check if this is the main category ID
      if (mainCategory.id === categoryId) {
        return mainCategory.color || defaultColors[0];
      }
      
      // Check subcategories
      if (mainCategory.subcategories) {
        const subcategory = mainCategory.subcategories.find((sub: any) => sub.id === categoryId);
        if (subcategory) {
          return subcategory.color || mainCategory.color || defaultColors[0];
        }
      }
    }
    
    // If category not found, return a default color based on category name hash
    const categoryName = getCategoryName(transaction);
    const hash = categoryName.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return defaultColors[Math.abs(hash) % defaultColors.length];
  };

  const updateConfig = (updates: Partial<ReportConfig>) => {
    setReportConfig(prev => ({ 
      ...prev, 
      ...updates,
      updatedAt: new Date()
    }));
  };

  const handleSave = async () => {
    if (!reportConfig.name?.trim()) {
      toast.error('Please enter a report name');
      return;
    }

    const config: ReportConfig = {
      ...reportConfig,
      period: isCustomDate && dateRange.from && dateRange.to 
        ? {
            label: 'Custom Period',
            value: 'custom',
            startDate: dateRange.from,
            endDate: dateRange.to
          }
        : reportConfig.period,
      updatedAt: new Date(),
    };

    try {
      await onSave?.(config);
      toast.success('Report saved successfully');
    } catch (error) {
      toast.error('Failed to save report');
    }
  };

  const nextStep = () => {
    if (currentStep < STEP_CONFIG.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: // Basics
        return reportConfig.name?.trim() && reportConfig.type;
      case 1: // Type
        return reportConfig.type && reportConfig.chartType;
      case 2: // Period
        return reportConfig.period || (isCustomDate && dateRange.from && dateRange.to);
      case 3: // Filters
        return true; // Filters are optional
      case 4: // Preview
        return true;
      default:
        return false;
    }
  };

  // Calculate preview data based on current configuration
  const previewData = useMemo(() => {
    if (!transactions.length) return null;

    // Filter transactions based on config
    let filteredTransactions = transactions.filter(t => {
      // Type filter
      if (!reportConfig.filters.transactionTypes.includes(t.type)) return false;
      
      // Amount filter
      const amount = typeof t.amount === 'string' ? parseFloat(t.amount) : t.amount;
      if (reportConfig.filters.minAmount && Math.abs(amount) < reportConfig.filters.minAmount) return false;
      if (reportConfig.filters.maxAmount && Math.abs(amount) > reportConfig.filters.maxAmount) return false;
      
      // Get main category name for filtering (excludes uncategorized)
      const mainCategoryName = getMainCategoryName(t);
      if (!mainCategoryName) return false; // Exclude uncategorized transactions
      
      // Category filter
      if (reportConfig.categories.length > 0 && !reportConfig.categories.includes(mainCategoryName)) return false;
      
      // Advanced category filtering (include/exclude)
      if (reportConfig.filters.includedCategories && reportConfig.filters.includedCategories.length > 0) {
        if (!reportConfig.filters.includedCategories.includes(mainCategoryName)) return false;
      }
      if (reportConfig.filters.excludedCategories && reportConfig.filters.excludedCategories.length > 0) {
        if (reportConfig.filters.excludedCategories.includes(mainCategoryName)) return false;
      }
      
      // Payee filtering (include/exclude)
      const payee = t.payee || t.merchant || t.description || '';
      if (reportConfig.filters.includedPayees && reportConfig.filters.includedPayees.length > 0) {
        if (!reportConfig.filters.includedPayees.includes(payee)) return false;
      }
      if (reportConfig.filters.excludedPayees && reportConfig.filters.excludedPayees.length > 0) {
        if (reportConfig.filters.excludedPayees.includes(payee)) return false;
      }
      
      return true;
    });

    // Calculate metrics with currency conversion
    let totalIncome = 0;
    let totalExpenses = 0;
    const categoryTotals: Record<string, number> = {};

    filteredTransactions.forEach(transaction => {
      const amount = typeof transaction.amount === 'string' 
        ? parseFloat(transaction.amount) 
        : transaction.amount;
      
      if (isNaN(amount)) return;

      // Get transaction currency and convert to default currency
      let transactionCurrency = 'USD'; // Default
      try {
        // Find the account for this transaction to get its currency
        const transactionAccount = accounts.find(acc => acc.id === transaction.account_id);
        if (transactionAccount?.currency) {
          transactionCurrency = transactionAccount.currency;
        }
      } catch (error) {
        // Use default currency if account lookup fails
      }

      // Convert to default currency
      const convertedAmount = convertToDefault 
        ? convertToDefault(Math.abs(amount), transactionCurrency)
        : Math.abs(amount);

      if (transaction.type === 'income') {
        totalIncome += convertedAmount;
      } else if (transaction.type === 'expense') {
        totalExpenses += convertedAmount;
        
        const mainCategoryName = getMainCategoryName(transaction);
        // Only include categorized transactions with main categories
        if (mainCategoryName) {
          categoryTotals[mainCategoryName] = (categoryTotals[mainCategoryName] || 0) + convertedAmount;
        }
      }
    });

    const netSavings = totalIncome - totalExpenses;
    const savingsRate = totalIncome > 0 ? (netSavings / totalIncome) * 100 : 0;
    const avgDailySpending = totalExpenses / 30; // Simplified

    const metrics = {
      totalIncome,
      totalExpenses,
      netSavings,
      avgDailySpending,
      savingsRate,
      transactionCount: filteredTransactions.length
    };

    // Category analysis for charts with main categories only
    // Create a mapping of main category names to colors by processing filtered transactions
    const categoryColorMap: Record<string, string> = {};
    
    filteredTransactions
      .filter(t => t.type === 'expense')
      .forEach(transaction => {
        const mainCategoryName = getMainCategoryName(transaction);
        if (mainCategoryName && !categoryColorMap[mainCategoryName]) {
          categoryColorMap[mainCategoryName] = getCategoryColor(transaction);
        }
      });

    // Process category data with smart grouping
    let processedCategoryAnalysis = Object.entries(categoryTotals)
      .map(([name, amount]) => ({
        id: name,
        name,
        amount,
        percentage: (amount / totalExpenses) * 100,
        transactionCount: 0,
        color: categoryColorMap[name] || '#3B82F6',
        trend: 0
      }))
      .sort((a, b) => b.amount - a.amount);

    // Group small categories (less than 2% each) into "Other"
    const significantCategories = processedCategoryAnalysis.filter(cat => cat.percentage >= 2);
    const smallCategories = processedCategoryAnalysis.filter(cat => cat.percentage < 2);
    
    if (smallCategories.length > 0) {
      const otherAmount = smallCategories.reduce((sum, cat) => sum + cat.amount, 0);
      const otherPercentage = smallCategories.reduce((sum, cat) => sum + cat.percentage, 0);
      
      if (otherAmount > 0) {
        significantCategories.push({
          id: 'other',
          name: `Other (${smallCategories.length} categories)`,
          amount: otherAmount,
          percentage: otherPercentage,
          transactionCount: 0,
          color: '#94A3B8', // Gray color for "Other"
          trend: 0
        });
      }
    }

    // Since we're working with main categories only, use significantCategories directly
    const categoryAnalysis = significantCategories;

    // Payee analysis for charts with currency conversion
    const payeeTotals: Record<string, number> = {};
    filteredTransactions.forEach(transaction => {
      if (transaction.type === 'expense') {
        const amount = typeof transaction.amount === 'string' 
          ? parseFloat(transaction.amount) 
          : transaction.amount;
        
        if (!isNaN(amount)) {
          // Get transaction currency and convert to default currency
          let transactionCurrency = 'USD'; // Default
          try {
            // Find the account for this transaction to get its currency
            const transactionAccount = accounts.find(acc => acc.id === transaction.account_id);
            if (transactionAccount?.currency) {
              transactionCurrency = transactionAccount.currency;
            }
          } catch (error) {
            // Use default currency if account lookup fails
          }

          // Convert to default currency
          const convertedAmount = convertToDefault 
            ? convertToDefault(Math.abs(amount), transactionCurrency)
            : Math.abs(amount);

          const payeeName = transaction.payee || transaction.merchant || transaction.description || 'Unknown';
          payeeTotals[payeeName] = (payeeTotals[payeeName] || 0) + convertedAmount;
        }
      }
    });

    // Default colors for payees (since they don't have saved colors like categories)
    const defaultColors = [
      '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
      '#8B5CF6', '#06B6D4', '#EC4899', '#F97316',
      '#A855F7', '#14B8A6', '#84CC16', '#6366F1'
    ];

    const payeeAnalysis = Object.entries(payeeTotals)
      .map(([name, amount], index) => ({
        id: name,
        name,
        amount,
        percentage: (amount / totalExpenses) * 100,
        transactionCount: 0,
        color: defaultColors[index % defaultColors.length],
        trend: 0
      }))
      .sort((a, b) => b.amount - a.amount);

    // Time series data (simplified monthly) with currency conversion
    const monthlyData: Record<string, { income: number; expenses: number }> = {};
    
    filteredTransactions.forEach(transaction => {
      const date = new Date(transaction.date);
      const monthKey = format(date, 'MMM yyyy');
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { income: 0, expenses: 0 };
      }

      const amount = typeof transaction.amount === 'string' 
        ? parseFloat(transaction.amount) 
        : transaction.amount;
      
      if (isNaN(amount)) return;

      // Get transaction currency and convert to default currency
      let transactionCurrency = 'USD'; // Default
      try {
        // Find the account for this transaction to get its currency
        const transactionAccount = accounts.find(acc => acc.id === transaction.account_id);
        if (transactionAccount?.currency) {
          transactionCurrency = transactionAccount.currency;
        }
      } catch (error) {
        // Use default currency if account lookup fails
      }

      // Convert to default currency
      const convertedAmount = convertToDefault 
        ? convertToDefault(Math.abs(amount), transactionCurrency)
        : Math.abs(amount);

      if (transaction.type === 'income') {
        monthlyData[monthKey].income += convertedAmount;
      } else if (transaction.type === 'expense') {
        monthlyData[monthKey].expenses += convertedAmount;
      }
    });

    const timeSeriesData = Object.entries(monthlyData)
      .map(([date, data]) => ({
        date,
        income: data.income,
        expenses: data.expenses,
        savings: data.income - data.expenses,
        balance: data.income - data.expenses
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return { metrics, categoryAnalysis, payeeAnalysis, timeSeriesData };
  }, [transactions, reportConfig, categories]);

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `${mainCurrencySymbol}${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `${mainCurrencySymbol}${(amount / 1000).toFixed(1)}K`;
    } else {
      return `${mainCurrencySymbol}${amount.toFixed(0)}`;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Basics
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2 mb-8">
              <Settings className="w-12 h-12 mx-auto text-primary dark:text-primary-dark" />
              <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                Let's start with the basics
              </h3>
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                Give your report a name and description to help you identify it later
              </p>
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="reportName" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                  Report Name *
                </Label>
                <Input
                  id="reportName"
                  value={reportConfig.name || ''}
                  onChange={(e) => updateConfig({ name: e.target.value })}
                  placeholder="e.g., Monthly Spending Analysis"
                  className="h-11 w-full rounded-xl border border-border bg-background-input px-3 py-2 text-text-primary shadow-sm focus:ring-2 focus:ring-secondary dark:border-border-dark dark:bg-background-dark-input dark:text-text-dark-primary dark:focus:ring-primary-dark"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                  Description (Optional)
                </Label>
                <Textarea
                  id="description"
                  value={reportConfig.description || ''}
                  onChange={(e) => updateConfig({ description: e.target.value })}
                  placeholder="Describe what this report shows and what insights you want to gain"
                  rows={3}
                  className="rounded-xl border border-border bg-background-input px-3 py-2 text-text-primary shadow-sm focus:ring-2 focus:ring-secondary dark:border-border-dark dark:bg-background-dark-input dark:text-text-dark-primary dark:focus:ring-primary-dark"
                />
              </div>
            </div>
          </div>
        );

      case 1: // Type
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2 mb-8">
              <BarChart3 className="w-12 h-12 mx-auto text-primary dark:text-primary-dark" />
              <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                What would you like to analyze?
              </h3>
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                Choose the type of analysis and visualization that best suits your needs
              </p>
            </div>

            <div className="space-y-6">
              <div className="space-y-4">
                <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                  Report Type
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {REPORT_TYPES.map((type) => {
                    const Icon = type.icon;
                    return (
                      <Card
                        key={type.value}
                        className={cn(
                          "p-4 cursor-pointer transition-all duration-200 border-2",
                          reportConfig.type === type.value
                            ? "border-primary dark:border-primary-dark bg-primary/5 dark:bg-primary-dark/5"
                            : "border-border dark:border-border-dark hover:border-secondary dark:hover:border-primary-dark"
                        )}
                        onClick={() => updateConfig({ type: type.value })}
                      >
                        <div className="flex items-start space-x-3">
                          <Icon className={cn("w-6 h-6 mt-1", type.color)} />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-text-primary dark:text-text-dark-primary">
                              {type.label}
                            </h4>
                            <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
                              {type.description}
                            </p>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-4">
                <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                  Chart Style
                </Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {CHART_TYPES.map((chart) => {
                    const Icon = chart.icon;
                    return (
                      <Card
                        key={chart.value}
                        className={cn(
                          "p-3 cursor-pointer transition-all duration-200 border-2",
                          reportConfig.chartType === chart.value
                            ? "border-primary dark:border-primary-dark bg-primary/5 dark:bg-primary-dark/5"
                            : "border-border dark:border-border-dark hover:border-secondary dark:hover:border-primary-dark"
                        )}
                        onClick={() => updateConfig({ chartType: chart.value })}
                      >
                        <div className="text-center space-y-2">
                          <Icon className="w-6 h-6 mx-auto text-primary dark:text-primary-dark" />
                          <div>
                            <div className="font-medium text-text-primary dark:text-text-dark-primary text-sm">
                              {chart.label}
                            </div>
                            <div className="text-xs text-text-secondary dark:text-text-dark-secondary mt-1">
                              {chart.description}
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );

      case 2: // Period
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2 mb-8">
              <CalendarIcon className="w-12 h-12 mx-auto text-primary dark:text-primary-dark" />
              <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                Select time period
              </h3>
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                Choose the date range for your analysis
              </p>
            </div>

            <div className="space-y-6">
              <div className="space-y-4">
                <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                  Time Period
                </Label>
                <Select
                  value={isCustomDate ? 'custom' : reportConfig.period?.value}
                  onValueChange={(value) => {
                    if (value === 'custom') {
                      setIsCustomDate(true);
                    } else {
                      setIsCustomDate(false);
                      const period = periods.find(p => p.value === value);
                      updateConfig({ period });
                    }
                  }}
                >
                  <SelectTrigger className="h-11 w-full rounded-xl border border-border bg-background-input px-3 py-2 text-text-primary shadow-sm dark:border-border-dark dark:bg-background-dark-input dark:text-text-dark-primary">
                    <SelectValue placeholder="Select time period" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl border border-border bg-background-secondary dark:border-border-dark dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150">
                    {periods.map((period) => (
                      <SelectItem key={period.value} value={period.value} className="rounded-lg px-2 py-1.5 text-sm hover:bg-secondary/10 hover:text-secondary dark:hover:bg-primary-dark/10 dark:hover:text-primary-dark">
                        {period.label}
                      </SelectItem>
                    ))}
                    <SelectItem value="custom" className="rounded-lg px-2 py-1.5 text-sm hover:bg-secondary/10 hover:text-secondary dark:hover:bg-primary-dark/10 dark:hover:text-primary-dark">Custom Period</SelectItem>
                  </SelectContent>
                </Select>

                {isCustomDate && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                        From Date
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="h-11 w-full bg-background-input dark:bg-background-dark-input text-text-primary dark:text-text-dark-primary border border-border dark:border-border-dark justify-start text-left font-normal rounded-xl"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {dateRange.from ? format(dateRange.from, 'MMM dd, yyyy') : 'Select start date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={dateRange.from}
                            onSelect={(date) => setDateRange(prev => ({ ...prev, from: date }))}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                        To Date
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="h-11 w-full bg-background-input dark:bg-background-dark-input text-text-primary dark:text-text-dark-primary border border-border dark:border-border-dark justify-start text-left font-normal rounded-xl"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {dateRange.to ? format(dateRange.to, 'MMM dd, yyyy') : 'Select end date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={dateRange.to}
                            onSelect={(date) => setDateRange(prev => ({ ...prev, to: date }))}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 3: // Filters
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2 mb-8">
              <Filter className="w-12 h-12 mx-auto text-primary dark:text-primary-dark" />
              <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                Customize your analysis
              </h3>
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                Set up filters and choose which metrics to include
              </p>
            </div>

            <div className="space-y-6">
              {/* Metrics Selection */}
              <div className="space-y-4">
                <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                  Include Metrics
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {availableMetrics.map((metric) => {
                    const Icon = metric.icon;
                    return (
                      <Card
                        key={metric.id}
                        className={cn(
                          "p-3 cursor-pointer transition-all duration-200 border",
                          reportConfig.includeMetrics.includes(metric.id)
                            ? "border-primary dark:border-primary-dark bg-primary/5 dark:bg-primary-dark/5"
                            : "border-border dark:border-border-dark hover:border-secondary dark:hover:border-primary-dark"
                        )}
                        onClick={() => {
                          const newMetrics = reportConfig.includeMetrics.includes(metric.id)
                            ? reportConfig.includeMetrics.filter(id => id !== metric.id)
                            : [...reportConfig.includeMetrics, metric.id];
                          updateConfig({ includeMetrics: newMetrics });
                        }}
                      >
                                                 <div className="flex items-start space-x-3">
                           <Checkbox
                             checked={reportConfig.includeMetrics.includes(metric.id)}
                             className="mt-1"
                           />
                          <Icon className="w-5 h-5 mt-0.5 text-primary dark:text-primary-dark" />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-text-primary dark:text-text-dark-primary text-sm">
                              {metric.label}
                            </h4>
                            <p className="text-xs text-text-secondary dark:text-text-dark-secondary mt-1">
                              {metric.description}
                            </p>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>

              <Separator />

              {/* Transaction Types */}
              <div className="space-y-4">
                <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                  Transaction Types
                </Label>
                <div className="flex flex-wrap gap-2">
                  {['income', 'expense', 'transfer'].map((type) => (
                    <Badge
                      key={type}
                      variant={reportConfig.filters.transactionTypes.includes(type as any) ? "default" : "outline"}
                      className={cn(
                        "cursor-pointer transition-all duration-200 capitalize px-3 py-1",
                        reportConfig.filters.transactionTypes.includes(type as any)
                          ? "bg-primary text-white dark:bg-primary-dark"
                          : "border-border dark:border-border-dark text-text-secondary dark:text-text-dark-secondary hover:border-primary dark:hover:border-primary-dark"
                      )}
                      onClick={() => {
                        const newTypes = reportConfig.filters.transactionTypes.includes(type as any)
                          ? reportConfig.filters.transactionTypes.filter(t => t !== type)
                          : [...reportConfig.filters.transactionTypes, type as any];
                        updateConfig({
                          filters: { ...reportConfig.filters, transactionTypes: newTypes }
                        });
                      }}
                    >
                      {type}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Amount Range */}
              <div className="space-y-4">
                <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                  Amount Range (Optional)
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-text-secondary dark:text-text-dark-secondary">
                      Minimum Amount
                    </Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={reportConfig.filters.minAmount || ''}
                      onChange={(e) => updateConfig({
                        filters: { ...reportConfig.filters, minAmount: Number(e.target.value) || undefined }
                      })}
                      className="h-10 rounded-xl border border-border bg-background-input px-3 py-2 text-text-primary shadow-sm dark:border-border-dark dark:bg-background-dark-input dark:text-text-dark-primary"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-text-secondary dark:text-text-dark-secondary">
                      Maximum Amount
                    </Label>
                    <Input
                      type="number"
                      placeholder="No limit"
                      value={reportConfig.filters.maxAmount || ''}
                      onChange={(e) => updateConfig({
                        filters: { ...reportConfig.filters, maxAmount: Number(e.target.value) || undefined }
                      })}
                      className="h-10 rounded-xl border border-border bg-background-input px-3 py-2 text-text-primary shadow-sm dark:border-border-dark dark:bg-background-dark-input dark:text-text-dark-primary"
                    />
                  </div>
                </div>
              </div>

              {/* Category Filtering - Always show for reports with categories */}
              {availableCategories.length > 0 && (
                <>
                  <Separator />
                  
                  {/* Advanced Category Filtering for Category Breakdown Reports */}
                  {reportConfig.type === 'category_breakdown' && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                          Category Selection
                        </Label>
                        <Badge variant="outline" className="text-xs">
                          {(reportConfig.filters.includedCategories?.length || 0) + (reportConfig.filters.excludedCategories?.length || 0)} rules
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Include Categories */}
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                              Include Categories ({reportConfig.filters.includedCategories?.length || 0})
                            </Label>
                          </div>
                          <div className="max-h-40 overflow-y-auto space-y-2 border border-border dark:border-border-dark rounded-lg p-3 bg-green-50/50 dark:bg-green-900/10">
                            {availableCategories.map((category) => (
                              <div key={`include-${category}`} className="flex items-center space-x-3">
                                <Checkbox
                                  id={`include-category-${category}`}
                                  checked={reportConfig.filters.includedCategories?.includes(category) || false}
                                  onCheckedChange={() => {
                                    const current = reportConfig.filters.includedCategories || [];
                                    const newIncluded = current.includes(category)
                                      ? current.filter(c => c !== category)
                                      : [...current, category];
                                    updateConfig({
                                      filters: { ...reportConfig.filters, includedCategories: newIncluded }
                                    });
                                  }}
                                />
                                <Label htmlFor={`include-category-${category}`} className="text-sm text-text-primary dark:text-text-dark-primary cursor-pointer flex-1">
                                  {category}
                                </Label>
                              </div>
                            ))}
                            {availableCategories.length === 0 && (
                              <p className="text-xs text-text-secondary dark:text-text-dark-secondary text-center py-2">
                                No categories found in transactions
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Exclude Categories */}
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                              Exclude Categories ({reportConfig.filters.excludedCategories?.length || 0})
                            </Label>
                          </div>
                          <div className="max-h-40 overflow-y-auto space-y-2 border border-border dark:border-border-dark rounded-lg p-3 bg-red-50/50 dark:bg-red-900/10">
                            {availableCategories.map((category) => (
                              <div key={`exclude-${category}`} className="flex items-center space-x-3">
                                <Checkbox
                                  id={`exclude-category-${category}`}
                                  checked={reportConfig.filters.excludedCategories?.includes(category) || false}
                                  onCheckedChange={() => {
                                    const current = reportConfig.filters.excludedCategories || [];
                                    const newExcluded = current.includes(category)
                                      ? current.filter(c => c !== category)
                                      : [...current, category];
                                    updateConfig({
                                      filters: { ...reportConfig.filters, excludedCategories: newExcluded }
                                    });
                                  }}
                                />
                                <Label htmlFor={`exclude-category-${category}`} className="text-sm text-text-primary dark:text-text-dark-primary cursor-pointer flex-1">
                                  {category}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                        <div className="flex items-start space-x-2">
                          <div className="w-4 h-4 text-blue-500 mt-0.5">ℹ️</div>
                          <div className="text-xs text-blue-700 dark:text-blue-300">
                            <p className="font-medium mb-1">How category filtering works:</p>
                            <ul className="space-y-1 text-blue-600 dark:text-blue-400">
                              <li>• If you select categories to <span className="font-medium text-green-600">include</span>, only those categories will appear in your report</li>
                              <li>• If you select categories to <span className="font-medium text-red-600">exclude</span>, those categories will be hidden from your report</li>
                              <li>• Include rules take priority over exclude rules</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Advanced Payee Filtering for Payee Analysis Reports */}
                  {reportConfig.type === 'payee_analysis' && availablePayees.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                          Payee Selection
                        </Label>
                        <Badge variant="outline" className="text-xs">
                          {(reportConfig.filters.includedPayees?.length || 0) + (reportConfig.filters.excludedPayees?.length || 0)} rules
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Include Payees */}
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                            <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                              Include Payees ({reportConfig.filters.includedPayees?.length || 0})
                            </Label>
                          </div>
                          <div className="max-h-40 overflow-y-auto space-y-2 border border-border dark:border-border-dark rounded-lg p-3 bg-green-50/50 dark:bg-green-900/10">
                            {availablePayees.slice(0, 20).map((payee) => (
                              <div key={`include-${payee}`} className="flex items-center space-x-3">
                                <Checkbox
                                  id={`include-payee-${payee}`}
                                  checked={reportConfig.filters.includedPayees?.includes(payee) || false}
                                  onCheckedChange={() => {
                                    const current = reportConfig.filters.includedPayees || [];
                                    const newIncluded = current.includes(payee)
                                      ? current.filter(p => p !== payee)
                                      : [...current, payee];
                                    updateConfig({
                                      filters: { ...reportConfig.filters, includedPayees: newIncluded }
                                    });
                                  }}
                                />
                                <Label htmlFor={`include-payee-${payee}`} className="text-sm text-text-primary dark:text-text-dark-primary cursor-pointer flex-1 truncate">
                                  {payee}
                                </Label>
                              </div>
                            ))}
                            {availablePayees.length > 20 && (
                              <p className="text-xs text-text-secondary dark:text-text-dark-secondary text-center py-2">
                                Showing top 20 payees. Use search to find specific payees.
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Exclude Payees */}
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                              Exclude Payees ({reportConfig.filters.excludedPayees?.length || 0})
                            </Label>
                          </div>
                          <div className="max-h-40 overflow-y-auto space-y-2 border border-border dark:border-border-dark rounded-lg p-3 bg-red-50/50 dark:bg-red-900/10">
                            {availablePayees.slice(0, 20).map((payee) => (
                              <div key={`exclude-${payee}`} className="flex items-center space-x-3">
                                <Checkbox
                                  id={`exclude-payee-${payee}`}
                                  checked={reportConfig.filters.excludedPayees?.includes(payee) || false}
                                  onCheckedChange={() => {
                                    const current = reportConfig.filters.excludedPayees || [];
                                    const newExcluded = current.includes(payee)
                                      ? current.filter(p => p !== payee)
                                      : [...current, payee];
                                    updateConfig({
                                      filters: { ...reportConfig.filters, excludedPayees: newExcluded }
                                    });
                                  }}
                                />
                                <Label htmlFor={`exclude-payee-${payee}`} className="text-sm text-text-primary dark:text-text-dark-primary cursor-pointer flex-1 truncate">
                                  {payee}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                        <div className="flex items-start space-x-2">
                          <div className="w-4 h-4 text-blue-500 mt-0.5">ℹ️</div>
                          <div className="text-xs text-blue-700 dark:text-blue-300">
                            <p className="font-medium mb-1">How payee filtering works:</p>
                            <ul className="space-y-1 text-blue-600 dark:text-blue-400">
                              <li>• If you select payees to <span className="font-medium text-green-600">include</span>, only transactions from those payees will appear</li>
                              <li>• If you select payees to <span className="font-medium text-red-600">exclude</span>, transactions from those payees will be hidden</li>
                              <li>• Include rules take priority over exclude rules</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Basic Category Filtering for Other Report Types */}
                  {reportConfig.type !== 'category_breakdown' && reportConfig.type !== 'payee_analysis' && (
                    <div className="space-y-4">
                      <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                        Filter by Categories ({reportConfig.categories.length} selected)
                      </Label>
                      <div className="max-h-32 overflow-y-auto space-y-2 border border-border dark:border-border-dark rounded-lg p-3">
                        {availableCategories.map((category) => (
                          <div key={category} className="flex items-center space-x-3">
                            <Checkbox
                              id={`basic-category-${category}`}
                              checked={reportConfig.categories.includes(category)}
                              onCheckedChange={() => {
                                const newCategories = reportConfig.categories.includes(category)
                                  ? reportConfig.categories.filter(c => c !== category)
                                  : [...reportConfig.categories, category];
                                updateConfig({ categories: newCategories });
                              }}
                            />
                            <Label htmlFor={`basic-category-${category}`} className="text-sm text-text-primary dark:text-text-dark-primary cursor-pointer flex-1">
                              {category}
                            </Label>
                          </div>
                        ))}
                      </div>
                      <div className="text-xs text-text-secondary dark:text-text-dark-secondary">
                        💡 Select categories to include in your analysis. Leave empty to include all categories.
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Show message if no categories are available */}
              {availableCategories.length === 0 && (
                <div className="text-center py-8 space-y-2">
                  <div className="text-text-secondary dark:text-text-dark-secondary">
                    📂 No categories found in your transactions
                  </div>
                  <div className="text-xs text-text-secondary dark:text-text-dark-secondary">
                    Categories will appear here once you have transactions with category data
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 4: // Preview
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2 mb-8">
              <Eye className="w-12 h-12 mx-auto text-primary dark:text-primary-dark" />
              <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                Preview your report
              </h3>
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                Review your report configuration and see a live preview
              </p>
            </div>

            {previewData && (
              <div className="space-y-6">
                {/* Report Summary */}
                <Card className="p-4 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border border-border dark:border-border-dark rounded-xl">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-text-primary dark:text-text-dark-primary">
                        {reportConfig.name}
                      </h4>
                      <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 dark:bg-primary-dark/10 dark:text-primary-dark dark:border-primary-dark/20">
                        {previewData.metrics.transactionCount} transactions
                      </Badge>
                    </div>
                    {reportConfig.description && (
                      <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                        {reportConfig.description}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2 text-xs text-text-secondary dark:text-text-dark-secondary">
                      <span>Type: {REPORT_TYPES.find(t => t.value === reportConfig.type)?.label}</span>
                      <span>•</span>
                      <span>Chart: {CHART_TYPES.find(c => c.value === reportConfig.chartType)?.label}</span>
                      <span>•</span>
                      <span>Period: {reportConfig.period?.label || 'Custom'}</span>
                    </div>
                  </div>
                </Card>

                {/* Preview Metrics */}
                {reportConfig.includeMetrics.length > 0 && (
                  <div className="space-y-4">
                    <h4 className="font-medium text-text-primary dark:text-text-dark-primary">
                      Key Metrics
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {reportConfig.includeMetrics.map((metricId) => {
                        const metric = availableMetrics.find(m => m.id === metricId);
                        if (!metric) return null;
                        
                        const Icon = metric.icon;
                        const value = previewData.metrics[metricId as keyof typeof previewData.metrics];
                        
                        return (
                          <MetricCard
                            key={metricId}
                            title={metric.label}
                            value={
                              metricId === 'savingsRate' 
                                ? `${(value as number).toFixed(1)}%`
                                : formatCurrency(value as number)
                            }
                            icon={<Icon className="w-5 h-5" />}
                            iconColor="text-primary dark:text-primary-dark"
                            iconBgColor="bg-primary/10 dark:bg-primary-dark/10"
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Preview Chart */}
                <div className="space-y-4">
                  <h4 className="font-medium text-text-primary dark:text-text-dark-primary">
                    Chart Preview
                  </h4>
                  <div className="bg-background-secondary/30 dark:bg-background-dark-secondary/30 rounded-xl p-4 border border-border dark:border-border-dark">
                    {reportConfig.type === 'category_breakdown' || reportConfig.type === 'payee_analysis' || reportConfig.chartType === 'pie' || reportConfig.chartType === 'doughnut' ? (
                      <Chart
                        type={reportConfig.chartType}
                        data={reportConfig.type === 'payee_analysis' ? previewData.payeeAnalysis : previewData.categoryAnalysis}
                        title={reportConfig.name}
                        subtitle={reportConfig.description}
                        height={300}
                        mainCurrencySymbol={mainCurrencySymbol}
                        mainCurrencyCode={defaultCurrency}
                      />
                    ) : (
                      <Chart
                        type={reportConfig.chartType}
                        data={previewData.timeSeriesData}
                        title={reportConfig.name}
                        subtitle={reportConfig.description}
                        height={300}
                        mainCurrencySymbol={mainCurrencySymbol}
                        mainCurrencyCode={defaultCurrency}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Step Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {STEP_CONFIG.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            
            return (
              <div key={step.id} className="flex items-center">
                <div
                  className={cn(
                    "flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-200",
                    isActive
                      ? "border-primary dark:border-primary-dark bg-primary dark:bg-primary-dark text-white"
                      : isCompleted
                      ? "border-primary dark:border-primary-dark bg-primary/10 dark:bg-primary-dark/10 text-primary dark:text-primary-dark"
                      : "border-border dark:border-border-dark text-text-secondary dark:text-text-dark-secondary"
                  )}
                >
                  <StepIcon className="w-5 h-5" />
                </div>
                {index < STEP_CONFIG.length - 1 && (
                  <div
                    className={cn(
                      "flex-1 h-0.5 mx-4 transition-all duration-200",
                      isCompleted
                        ? "bg-primary dark:bg-primary-dark"
                        : "bg-border dark:bg-border-dark"
                    )}
                  />
                )}
              </div>
            );
          })}
        </div>
        
        <div className="text-center">
          <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">
            {STEP_CONFIG[currentStep].title}
          </h2>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
            {STEP_CONFIG[currentStep].description}
          </p>
        </div>
      </div>

      {/* Step Content */}
      <Card className="bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark shadow-sm rounded-xl">
        <div className="p-6 lg:p-8">
          {renderStepContent()}
        </div>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8">
        <Button
          variant="outline"
          onClick={currentStep === 0 ? onCancel : prevStep}
          className="bg-background-secondary dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl hover:bg-secondary/10 hover:text-secondary dark:hover:bg-primary-dark/10 dark:hover:text-primary-dark"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {currentStep === 0 ? 'Cancel' : 'Previous'}
        </Button>

        <div className="flex items-center space-x-2 text-sm text-text-secondary dark:text-text-dark-secondary">
          <span>Step {currentStep + 1} of {STEP_CONFIG.length}</span>
        </div>

        {currentStep === STEP_CONFIG.length - 1 ? (
          <Button
            onClick={handleSave}
            disabled={!canProceed() || isLoading}
            className="bg-primary text-white hover:bg-primary/90 shadow-sm rounded-xl dark:bg-primary-dark dark:hover:bg-primary-dark/90"
          >
            <Save className="w-4 h-4 mr-2" />
            Save Report
          </Button>
        ) : (
          <Button
            onClick={nextStep}
            disabled={!canProceed()}
            className="bg-primary text-white hover:bg-primary/90 shadow-sm rounded-xl dark:bg-primary-dark dark:hover:bg-primary-dark/90"
          >
            Next
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
} 